/**
 * BigSplice Chat Visualization Component
 * Lightweight embedded 3D visualization for chat messages
 */

import React, { Suspense, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Loader2, Maximize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

// Inline type definitions
type DataRow = Record<string, string | number | boolean | null>;
type LayoutType = 'bars' | 'donut' | 'pie' | 'scatter' | 'spiral' | 'grid' | 'line';

interface ChatVisualizationProps {
  data: DataRow[];
  layout: LayoutType;
  xAxis?: string;
  colorColumn?: string;
  height?: number;
}

export const ChatVisualization: React.FC<ChatVisualizationProps> = ({
  data,
  layout,
  xAxis,
  colorColumn,
  height = 300,
}) => {
  const navigate = useNavigate();

  // Color palette (ocean theme)
  const colors = useMemo(
    () => [
      '#0077b6',
      '#00b4d8',
      '#48cae4',
      '#90e0ef',
      '#ade8f4',
      '#caf0f8',
    ],
    []
  );

  // Process data for visualization
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const xAxisKey = xAxis || Object.keys(data[0])[0];
    const colorKey = colorColumn || xAxisKey;

    // Get unique values for color mapping
    const uniqueColors = Array.from(new Set(data.map((d) => String(d[colorKey]))));
    const colorMap = new Map<string, string>();
    uniqueColors.forEach((value, index) => {
      colorMap.set(value, colors[index % colors.length]);
    });

    return data.map((row, index) => ({
      ...row,
      _index: index,
      _xValue: String(row[xAxisKey] || ''),
      _color: colorMap.get(String(row[colorKey])) || colors[0],
    }));
  }, [data, xAxis, colorColumn, colors]);

  // Handle opening full visualization
  const handleOpenFull = () => {
    // Store data in session storage for the Visualize page
    sessionStorage.setItem(
      'bigsplice-visualize-data',
      JSON.stringify({
        data,
        layout,
        xAxis,
        colorColumn,
        timestamp: Date.now(),
      })
    );
    navigate('/visualize');
  };

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-muted/50 rounded-lg"
        style={{ height }}
      >
        <p className="text-muted-foreground">No data to visualize</p>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden border bg-background">
      {/* 3D Canvas */}
      <div style={{ height }}>
        <Canvas camera={{ position: [0, 8, 20], fov: 50 }}>
          <Suspense fallback={null}>
            <ambientLight intensity={0.6} />
            <pointLight position={[10, 10, 10]} intensity={0.8} />
            <pointLight position={[-10, -10, -10]} intensity={0.3} />

            <SimpleBars data={processedData} layout={layout} />

            <OrbitControls
              enableZoom={true}
              enablePan={false}
              enableRotate={true}
              minDistance={5}
              maxDistance={50}
              autoRotate
              autoRotateSpeed={0.5}
            />
          </Suspense>
        </Canvas>
      </div>

      {/* Open Full Button */}
      <Button
        variant="secondary"
        size="sm"
        className="absolute top-2 right-2 h-7 text-xs"
        onClick={handleOpenFull}
      >
        <Maximize2 className="h-3 w-3 mr-1" />
        Full View
      </Button>

      {/* Controls hint */}
      <div className="absolute bottom-2 left-2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
        Drag to rotate | Scroll to zoom
      </div>
    </div>
  );
};

// Simple 3D bars component
interface ProcessedDataRow extends DataRow {
  _index: number;
  _xValue: string;
  _color: string;
}

const SimpleBars: React.FC<{
  data: ProcessedDataRow[];
  layout: LayoutType;
}> = ({ data, layout }) => {
  // Calculate positions based on layout
  const bars = useMemo(() => {
    const numBars = Math.min(data.length, 50); // Limit for performance
    const spacing = 1.5;

    return data.slice(0, numBars).map((row, index) => {
      // Get numeric value for height (use first numeric column or index)
      const numericKeys = Object.keys(row).filter(
        (k) => !k.startsWith('_') && typeof row[k] === 'number'
      );
      const heightValue =
        numericKeys.length > 0
          ? Math.abs(Number(row[numericKeys[0]]) || 0)
          : index + 1;

      // Normalize height (0.5 to 5 range)
      const maxHeight = Math.max(
        ...data.slice(0, numBars).map((d) => {
          const keys = Object.keys(d).filter(
            (k) => !k.startsWith('_') && typeof d[k] === 'number'
          );
          return keys.length > 0 ? Math.abs(Number(d[keys[0]]) || 0) : 1;
        })
      );
      const normalizedHeight = (heightValue / maxHeight) * 4 + 0.5;

      // Position based on layout
      let x = 0;
      let z = 0;

      switch (layout) {
        case 'bars':
        default:
          // Grid layout
          const cols = Math.ceil(Math.sqrt(numBars));
          x = (index % cols - cols / 2) * spacing;
          z = (Math.floor(index / cols) - Math.ceil(numBars / cols) / 2) * spacing;
          break;
        case 'donut':
          // Circular layout
          const angle = (index / numBars) * Math.PI * 2;
          const radius = 5;
          x = Math.cos(angle) * radius;
          z = Math.sin(angle) * radius;
          break;
        case 'line':
          // Linear layout
          x = (index - numBars / 2) * spacing * 0.8;
          z = 0;
          break;
        case 'scatter':
          // Random scatter (using index as seed)
          x = ((index * 7) % 20) - 10;
          z = ((index * 13) % 20) - 10;
          break;
      }

      return {
        position: [x, normalizedHeight / 2, z] as [number, number, number],
        height: normalizedHeight,
        color: row._color,
        label: row._xValue,
      };
    });
  }, [data, layout]);

  return (
    <group>
      {bars.map((bar, index) => (
        <mesh key={index} position={bar.position}>
          <boxGeometry args={[0.8, bar.height, 0.8]} />
          <meshStandardMaterial color={bar.color} />
        </mesh>
      ))}

      {/* Ground plane */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[30, 30]} />
        <meshStandardMaterial color="#1a1a2e" transparent opacity={0.3} />
      </mesh>
    </group>
  );
};

// Loading fallback
export const VisualizationLoading: React.FC<{ height?: number }> = ({
  height = 300,
}) => {
  return (
    <div
      className="flex items-center justify-center bg-muted/50 rounded-lg"
      style={{ height }}
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
};

export default ChatVisualization;
